import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Pencil, Trash2, X, Copy, Eye, Send, Ban, CheckCircle2, Upload,
  Users, Calendar, Clock, MapPin, Video, Star, Loader2, ImageOff,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Avatar from "@/components/Avatar";

const CATEGORIES = [
  "Book Discussion", "Brunch & Boundaries", "Wine Down & Write", "Pages & Pajamas",
  "Dinner Discussion", "Author Conversation", "Reading Night", "Self-Care Gathering",
  "Cultural Experience", "Social Gathering", "Writing Workshop", "Gathering",
];

const STATUS_STYLES = {
  draft: "text-caramel border-caramel/40 bg-caramel/10",
  published: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  cancelled: "text-red-300 border-red-400/40 bg-red-400/10",
  completed: "text-cream/60 border-cream/25 bg-cream/5",
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return "No date";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

const EMPTY = {
  title: "", type: "Gathering", date: "", time: "", end_time: "",
  location: "", address: "", city: "", state: "", is_virtual: false, virtual_link: "",
  description: "", instructions: "", image_url: "", book_id: "", capacity: 30,
  rsvp_enabled: true, registration_deadline: "", member_only: false, featured: false, status: "draft",
};

/* ---------- Modal ---------- */
function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="event-modal">
      <div className={`bg-mahogany-950 gold-frame rounded-sm w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto p-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-cream">{title}</h3>
          <button onClick={onClose} className="text-cream/60 hover:text-fuchsia" data-testid="event-modal-close"><X size={22} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Image Uploader ---------- */
function ImageUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    if (!/\.(jpe?g|png|webp|gif)$/i.test(file.name)) { toast.error("Please choose a JPG, PNG, WEBP, or GIF."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/event-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <label className="text-cream/50 text-xs uppercase tracking-wider block mb-2">Event Image</label>
      <div className="flex items-start gap-4">
        <div className="w-32 h-24 rounded-sm overflow-hidden bg-mahogany-800/60 gold-frame flex items-center justify-center shrink-0">
          {value ? <img src={value} alt="preview" className="w-full h-full object-cover" data-testid="event-image-preview" />
            : <ImageOff size={22} className="text-cream/30" />}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="event-image-input" />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className="btn-secondary !py-2 !px-4 !text-xs inline-flex items-center gap-2" data-testid="event-image-upload-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {value ? "Replace Image" : "Upload Image"}
          </button>
          {value && <button type="button" onClick={() => onChange("")} className="text-cream/40 text-xs hover:text-fuchsia ml-3" data-testid="event-image-remove">Remove</button>}
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL"
            className="input-line !text-xs" data-testid="event-image-url" />
          <p className="text-cream/30 text-[10px]">JPG, PNG, WEBP or GIF · up to 5MB</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Event Form ---------- */
function EventForm({ initial, books, onSaved, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async (statusOverride) => {
    if (!f.title?.trim()) { toast.error("Please add an event title."); return; }
    if (!f.date) { toast.error("Please choose a date."); return; }
    const payload = { ...f };
    delete payload._id; delete payload.rsvp_count; delete payload.book;
    payload.capacity = parseInt(payload.capacity, 10) || 0;
    if (statusOverride) payload.status = statusOverride;
    setSaving(true);
    try {
      const saved = f.id ? (await api.put(`/events/${f.id}`, payload)).data : (await api.post("/events", payload)).data;
      toast.success(statusOverride === "published" ? "Event published." : "Event saved.");
      onSaved(saved);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const L = ({ children }) => <label className="text-cream/50 text-xs uppercase tracking-wider block mb-1">{children}</label>;

  return (
    <div className="space-y-5" data-testid="event-form">
      <div><L>Event Title</L><input value={f.title} onChange={(e) => set("title", e.target.value)} className="input-line" data-testid="event-title" /></div>

      <div className="grid grid-cols-2 gap-4">
        <div><L>Category</L>
          <select value={f.type} onChange={(e) => set("type", e.target.value)} className="input-line" data-testid="event-category">
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-mahogany-900">{c}</option>)}
          </select>
        </div>
        <div><L>Associated Book (optional)</L>
          <select value={f.book_id || ""} onChange={(e) => set("book_id", e.target.value)} className="input-line" data-testid="event-book">
            <option value="" className="bg-mahogany-900">None</option>
            {books.map((b) => <option key={b.id} value={b.id} className="bg-mahogany-900">{b.title}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div><L>Date</L><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="input-line" data-testid="event-date" /></div>
        <div><L>Start Time</L><input value={f.time} onChange={(e) => set("time", e.target.value)} placeholder="6:30 PM" className="input-line" data-testid="event-time" /></div>
        <div><L>End Time</L><input value={f.end_time} onChange={(e) => set("end_time", e.target.value)} placeholder="8:30 PM" className="input-line" data-testid="event-endtime" /></div>
      </div>

      <label className="flex items-center gap-3 text-cream/80 text-sm">
        <input type="checkbox" checked={!!f.is_virtual} onChange={(e) => set("is_virtual", e.target.checked)} className="accent-fuchsia w-5 h-5" data-testid="event-virtual" />
        This is a virtual event
      </label>

      {f.is_virtual ? (
        <div><L>Virtual Meeting Link / Details</L><input value={f.virtual_link} onChange={(e) => set("virtual_link", e.target.value)} placeholder="Zoom link (shared with attendees)" className="input-line" data-testid="event-virtual-link" /></div>
      ) : (
        <div className="space-y-4">
          <div><L>Location Name</L><input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="The Ivy Room" className="input-line" data-testid="event-location" /></div>
          <div><L>Street Address</L><input value={f.address} onChange={(e) => set("address", e.target.value)} className="input-line" data-testid="event-address" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><L>City</L><input value={f.city} onChange={(e) => set("city", e.target.value)} className="input-line" data-testid="event-city" /></div>
            <div><L>State</L><input value={f.state} onChange={(e) => set("state", e.target.value)} className="input-line" data-testid="event-state" /></div>
          </div>
        </div>
      )}

      <div><L>Description</L><textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className="input-line resize-none" data-testid="event-description" /></div>

      <ImageUploader value={f.image_url} onChange={(v) => set("image_url", v)} />

      <div className="grid grid-cols-2 gap-4">
        <div><L>Maximum Capacity</L><input type="number" min="0" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} className="input-line" data-testid="event-capacity" /></div>
        <div><L>Registration Deadline</L><input type="date" value={f.registration_deadline} onChange={(e) => set("registration_deadline", e.target.value)} className="input-line" data-testid="event-deadline" /></div>
      </div>

      <div><L>Additional Instructions (optional)</L><textarea rows={2} value={f.instructions} onChange={(e) => set("instructions", e.target.value)} className="input-line resize-none" placeholder="What to bring, dress code, parking…" data-testid="event-instructions" /></div>

      <div className="flex flex-wrap gap-x-6 gap-y-3 pt-1">
        <label className="flex items-center gap-2 text-cream/80 text-sm"><input type="checkbox" checked={!!f.rsvp_enabled} onChange={(e) => set("rsvp_enabled", e.target.checked)} className="accent-fuchsia w-5 h-5" data-testid="event-rsvp-enabled" /> RSVPs enabled</label>
        <label className="flex items-center gap-2 text-cream/80 text-sm"><input type="checkbox" checked={!!f.member_only} onChange={(e) => set("member_only", e.target.checked)} className="accent-fuchsia w-5 h-5" data-testid="event-member-only" /> Members only</label>
        <label className="flex items-center gap-2 text-cream/80 text-sm"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-fuchsia w-5 h-5" data-testid="event-featured" /> Featured event</label>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gold/15">
        <button onClick={() => save()} disabled={saving} className="btn-secondary flex-1 inline-flex items-center justify-center gap-2" data-testid="event-save-draft">
          {saving && <Loader2 size={15} className="animate-spin" />} Save {f.status === "published" ? "Changes" : "as Draft"}
        </button>
        {f.status !== "published" && (
          <button onClick={() => save("published")} disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="event-save-publish">
            <Send size={15} /> Publish Now
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Attendee list ---------- */
function AttendeeModal({ event, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { if (event) api.get(`/events/${event.id}/rsvps`).then(({ data }) => setRows(data || [])); }, [event]);
  if (!event) return null;
  return (
    <Modal open onClose={onClose} title={`Attendees — ${event.title}`} wide>
      <div data-testid="attendee-list">
        <p className="text-cream/60 text-sm mb-5">{rows ? rows.length : "…"} of {event.capacity} spots filled</p>
        {!rows ? <p className="text-cream/50">Loading…</p> : rows.length === 0 ? (
          <p className="text-cream/50 py-8 text-center">No RSVPs yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 bg-mahogany-800/40 gold-frame rounded-sm p-3" data-testid={`attendee-${r.user_id}`}>
                <Avatar src={r.picture} name={r.name} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="text-cream truncate">{r.name}</p>
                  <p className="text-cream/50 text-sm truncate">{r.email}{[r.city, r.state].filter(Boolean).length ? ` · ${[r.city, r.state].filter(Boolean).join(", ")}` : ""}</p>
                </div>
                <span className="text-cream/40 text-xs shrink-0">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------- Row ---------- */
function EventRow({ e, onEdit, onStatus, onDuplicate, onDelete, onAttendees }) {
  const IconAction = ({ onClick, title, tone = "gold", testid, children }) => (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
        tone === "red" ? "border-red-400/30 text-red-400 hover:bg-red-400 hover:text-mahogany-900"
        : tone === "fuchsia" ? "border-fuchsia/40 text-fuchsia hover:bg-fuchsia hover:text-white"
        : "border-gold/30 text-gold hover:bg-gold hover:text-mahogany-900"}`}>
      {children}
    </button>
  );
  return (
    <div className="flex items-center justify-between bg-mahogany-800/40 gold-frame rounded-sm p-4" data-testid={`event-row-${e.id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-14 h-14 rounded-sm overflow-hidden bg-mahogany-900 shrink-0 flex items-center justify-center">
          {e.image_url ? <img src={e.image_url} alt="" className="w-full h-full object-cover" /> : <Calendar size={18} className="text-cream/30" />}
        </div>
        <div className="min-w-0">
          <p className="text-cream truncate flex items-center gap-2">
            {e.featured && <Star size={13} className="text-gold fill-gold shrink-0" />}
            {e.title}
            <span className={`text-[9px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_STYLES[e.status] || STATUS_STYLES.draft}`}>{e.status}</span>
          </p>
          <p className="text-cream/50 text-sm truncate flex items-center gap-3 mt-0.5">
            <span>{e.type}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(e.date)}</span>
            {e.is_virtual ? <span className="flex items-center gap-1"><Video size={12} /> Virtual</span> : e.location && <span className="flex items-center gap-1 truncate"><MapPin size={12} /> {e.location}</span>}
            <span className="flex items-center gap-1"><Users size={12} /> {e.rsvp_count || 0}/{e.capacity}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">
        <IconAction onClick={() => onAttendees(e)} title="View RSVPs" testid={`event-attendees-${e.id}`}><Users size={15} /></IconAction>
        <Link to={`/events/${e.id}`} target="_blank" title="Preview" data-testid={`event-preview-${e.id}`} className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-mahogany-900"><Eye size={15} /></Link>
        {e.status === "draft" && <IconAction onClick={() => onStatus(e, "published")} title="Publish" tone="fuchsia" testid={`event-publish-${e.id}`}><Send size={15} /></IconAction>}
        {e.status === "published" && <IconAction onClick={() => onStatus(e, "draft")} title="Unpublish" testid={`event-unpublish-${e.id}`}><Ban size={15} /></IconAction>}
        {(e.status === "published" || e.status === "draft") && <IconAction onClick={() => onStatus(e, "completed")} title="Mark completed" testid={`event-complete-${e.id}`}><CheckCircle2 size={15} /></IconAction>}
        {e.status !== "cancelled" && <IconAction onClick={() => onStatus(e, "cancelled")} title="Cancel event" tone="red" testid={`event-cancel-${e.id}`}><Ban size={15} /></IconAction>}
        <IconAction onClick={() => onDuplicate(e)} title="Duplicate" testid={`event-duplicate-${e.id}`}><Copy size={15} /></IconAction>
        <IconAction onClick={() => onEdit(e)} title="Edit" testid={`event-edit-${e.id}`}><Pencil size={15} /></IconAction>
        <IconAction onClick={() => onDelete(e)} title="Delete" tone="red" testid={`event-delete-${e.id}`}><Trash2 size={15} /></IconAction>
      </div>
    </div>
  );
}

/* ---------- Main ---------- */
export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [books, setBooks] = useState([]);
  const [view, setView] = useState("upcoming");
  const [editing, setEditing] = useState(null);
  const [attendees, setAttendees] = useState(null);

  const load = useCallback(() => api.get("/admin/events").then(({ data }) => setEvents(data || [])), []);
  useEffect(() => { load(); api.get("/books").then(({ data }) => setBooks(data || [])); }, [load]);

  const t = todayStr();
  const buckets = {
    upcoming: events.filter((e) => e.status === "published" && (e.date || "") >= t),
    drafts: events.filter((e) => e.status === "draft"),
    past: events.filter((e) => e.status === "completed" || (e.status === "published" && (e.date || "") < t)),
    cancelled: events.filter((e) => e.status === "cancelled"),
  };
  const TABS = [
    ["upcoming", "Upcoming"], ["drafts", "Drafts"], ["past", "Past / Our Gatherings"], ["cancelled", "Cancelled"],
  ];

  const onStatus = async (e, status) => {
    if (status === "cancelled" && !window.confirm(`Cancel "${e.title}"? It will be removed from the public site.`)) return;
    try { await api.put(`/events/${e.id}/status`, { status }); toast.success(`Event ${status}.`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const onDuplicate = async (e) => { await api.post(`/events/${e.id}/duplicate`); toast.success("Event duplicated as a draft."); setView("drafts"); load(); };
  const onDelete = async (e) => { if (!window.confirm(`Permanently delete "${e.title}"? This also removes its RSVPs.`)) return; await api.delete(`/events/${e.id}`); toast("Event deleted."); load(); };

  const list = buckets[view];

  return (
    <div data-testid="admin-events">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`events-subtab-${k}`}
              className={`rounded-full border px-4 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-colors ${
                view === k ? "bg-gold text-mahogany-900 border-gold" : "border-gold/30 text-cream/70 hover:text-gold"}`}>
              {label} <span className="opacity-70">{buckets[k].length}</span>
            </button>
          ))}
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2.5 !px-5 inline-flex items-center gap-2" data-testid="add-event"><Plus size={16} /> New Event</button>
      </div>

      {list.length === 0 ? (
        <p className="text-cream/50 text-center py-16" data-testid="events-empty-bucket">No {view === "past" ? "past" : view} events yet.</p>
      ) : (
        <div className="space-y-3">
          {list.map((e) => (
            <EventRow key={e.id} e={e} onEdit={setEditing} onStatus={onStatus} onDuplicate={onDuplicate} onDelete={onDelete} onAttendees={setAttendees} />
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit Event" : "New Event"} wide>
        {editing && <EventForm initial={editing} books={books} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </Modal>

      <AttendeeModal event={attendees} onClose={() => setAttendees(null)} />
    </div>
  );
}
